import { Linking, Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { api, type CurrentUser } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// The five things this screen can actually show. permission-denied is
// a specific, typed failure of the same mutation (see
// Tier0CameraPermissionDeniedError below), not a separate I/O source --
// and retry-vs-passed both come back as a perfectly ordinary mutation
// success, distinguished only by reading the response body's
// tier0Status. That's real branching logic beyond
// isPending/isError/isSuccess, so it's extracted and unit-tested here
// rather than left as inline conditionals in the component, mirroring
// use-auth-state.ts's deriveAuthState/useAuthState split.
export type Tier0Phase =
  | { phase: 'idle' }
  | { phase: 'permission-denied' }
  | { phase: 'submitting' }
  | { phase: 'retry' }
  | { phase: 'passed' }
  | { phase: 'error'; message: string };

// Thrown by the mutation below when the camera permission prompt is
// declined -- mirrors use-current-location.ts's
// LocationPermissionDeniedError exactly, and for the same reason: an
// earlier version tracked denial in separate React state, set only on
// the *resolved* "not granted" case, so any exception thrown by
// requestCameraPermissionsAsync or launchCameraAsync itself (not just a
// denial) was an unhandled promise rejection with no UI feedback --
// capture() below is fire-and-forget from onPress, awaited by nothing.
// Folding the whole permission-request-through-capture sequence into
// one mutationFn means every failure is now that mutation's isError,
// with nothing left outside it to silently disagree.
export class Tier0CameraPermissionDeniedError extends Error {}

// Thrown when the user backs out of the camera, or the returned asset
// somehow has no base64 payload (base64: true guarantees this in
// practice; this is a defensive guard against POSTing `undefined`, not
// a real user-facing path) -- not a real failure, just "nothing
// happened, go back to idle", so deriveTier0Phase maps this one
// specifically back to 'idle' rather than showing an error the user
// didn't cause.
export class Tier0CaptureCancelledError extends Error {}

// Pure: given the submitTier0 mutation's current result, what should
// the screen show? Kept separate from the hook below so the branching
// is unit-testable without rendering a React tree or driving
// expo-image-picker's mocked native module.
export function deriveTier0Phase(
  mutation: Pick<UseMutationResult<CurrentUser, Error, void>, 'isPending' | 'isError' | 'error' | 'isSuccess' | 'data'>,
): Tier0Phase {
  if (mutation.isPending) return { phase: 'submitting' };

  if (mutation.isError) {
    if (mutation.error instanceof Tier0CameraPermissionDeniedError) {
      return { phase: 'permission-denied' };
    }

    if (mutation.error instanceof Tier0CaptureCancelledError) {
      return { phase: 'idle' };
    }

    return {
      phase: 'error',
      message: mutation.error?.message || 'Something went wrong. Check your connection and try again.',
    };
  }

  if (mutation.isSuccess && mutation.data) {
    return { phase: mutation.data.tier0Status === 'tier0_passed' ? 'passed' : 'retry' };
  }

  return { phase: 'idle' };
}

export function useTier0State(): {
  phase: Tier0Phase;
  capture: () => void;
  openSettings: () => void;
  canOpenSettings: boolean;
} {
  const queryClient = useQueryClient();

  const submit = useMutation({
    // Permission request through the actual submit call all live in one
    // mutationFn (see Tier0CameraPermissionDeniedError/
    // Tier0CaptureCancelledError above) so every failure -- denied,
    // cancelled, or a real thrown exception from any of these native
    // calls -- becomes this mutation's own isError, with no state left
    // outside it that could silently disagree.
    mutationFn: async (): Promise<CurrentUser> => {
      // Requested eagerly, every attempt -- not cached in local state --
      // because iOS/Android both report the live OS-level grant, and a
      // user who denies once then enables it from Settings should have
      // the very next tap succeed without any extra "try again" step.
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (!permission.granted) {
        throw new Tier0CameraPermissionDeniedError();
      }

      // launchCameraAsync, not launchImageLibraryAsync: Tier 0 is a
      // liveness check, and a gallery pick defeats the entire point even
      // though the backend's stub checker doesn't verify liveness yet
      // (see docs/internal/design.md) -- the UI has to behave correctly
      // for when it does. cameraType: front matches what the API field
      // (selfieBase64) and the check itself actually need -- a photo of
      // the account holder's own face, not whatever the rear camera
      // happens to be pointed at.
      const result = await ImagePicker.launchCameraAsync({
        base64: true,
        quality: 0.7,
        cameraType: ImagePicker.CameraType.front,
      });
      if (result.canceled) {
        throw new Tier0CaptureCancelledError();
      }

      const asset = result.assets[0];
      if (!asset?.base64) {
        throw new Tier0CaptureCancelledError();
      }

      return api.submitTier0(asset.base64);
    },
    onSuccess: (user) => {
      // Only invalidate on an actual pass -- invalidating on
      // tier0_pending would just refetch the same unchanged status and
      // burn a network round trip for nothing. Since #70 (Tier 0 is
      // opt-in), this no longer flips any nav gate: the invalidation
      // refreshes the user's tier0Status so the Profile surface's
      // Verified badge appears when the user returns to it.
      if (user.tier0Status === 'tier0_passed') {
        queryClient.invalidateQueries({ queryKey: queryKeys.me() });
      }
    },
  });

  function capture() {
    if (submit.isPending) return;
    submit.mutate();
  }

  function openSettings() {
    // react-native-web's Linking has no openSettings implementation at
    // all (there's no per-site "app settings" screen the OS-level call
    // maps to on web) -- canOpenSettings below gates the button that
    // would call this out of the render entirely on web.
    void Linking.openSettings();
  }

  return {
    phase: deriveTier0Phase(submit),
    capture,
    openSettings,
    canOpenSettings: Platform.OS !== 'web',
  };
}
